import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, RouterProvider } from 'react-router';
import { ApiError } from './api/client.ts';
import { AuthProvider } from './auth/AuthProvider.tsx';
import './i18n/index.ts';
import './index.css';
import { AppShell } from './layout/AppShell.tsx';
import { MyAccountPage } from './pages/MyAccountPage.tsx';
import { HomePage } from './pages/HomePage.tsx';
import { NotFoundPage } from './pages/NotFoundPage.tsx';
import { TenantProvider } from './tenant/TenantProvider.tsx';
import { recoverLazyImport } from './layout/RouteState.tsx';
import { GlobalNavigationGuard, UnsavedProvider } from './performance/unsaved.tsx';

// L'administration est chargée à la demande : la coque reste légère pour les commerciaux terrain.
const GuideLibraryPage = lazy(() => recoverLazyImport(() => import('./help/GuideLibraryPage.tsx').then((m) => ({ default: m.GuideLibraryPage }))));
const TrainingPage = lazy(() => recoverLazyImport(() => import('./help/TrainingPage.tsx').then((m) => ({ default: m.TrainingPage }))));
const AdminHub = lazy(() => recoverLazyImport(() => import('./admin/AdminHub.tsx').then((m) => ({ default: m.AdminHub }))));
const VisitsRoutes = lazy(() => recoverLazyImport(() => import('./visits/VisitsRoutes.tsx').then((m) => ({ default: m.VisitsRoutes }))));
const ImportRoutes = lazy(() => recoverLazyImport(() => import('./imports/ImportRoutes.tsx').then((m) => ({ default: m.ImportRoutes }))));
const SalesRoutes = lazy(() => recoverLazyImport(() => import('./sales/SalesRoutes.tsx').then((m) => ({ default: m.SalesRoutes }))));
const CatalogRoutes = lazy(() => recoverLazyImport(() => import('./catalog/CatalogRoutes.tsx').then((m) => ({ default: m.CatalogRoutes }))));
const ReceivablesRoutes = lazy(() => recoverLazyImport(() => import('./receivables/ReceivablesRoutes.tsx').then((m) => ({ default: m.ReceivablesRoutes }))));
const PerformanceRoutes = lazy(() => recoverLazyImport(() => import('./performance/PerformanceRoutes.tsx').then((m) => ({ default: m.PerformanceRoutes }))));
const ComplaintsRoutes = lazy(() => recoverLazyImport(() => import('./complaints/ComplaintsRoutes.tsx').then((m) => ({ default: m.ComplaintsRoutes }))));
const AnalyticsRoutes = lazy(() => recoverLazyImport(() => import('./analytics/AnalyticsRoutes.tsx').then((m) => ({ default: m.AnalyticsRoutes }))));
const AccountsRoutes = lazy(() => recoverLazyImport(() => import('./accounts/AccountsRoutes.tsx').then((m) => ({ default: m.AccountsRoutes }))));
const LivestockRoutes = lazy(() => recoverLazyImport(() => import('./livestock/LivestockRoutes.tsx').then((m) => ({ default: m.LivestockRoutes }))));
const LogisticsRoutes = lazy(() => recoverLazyImport(() => import('./logistics/LogisticsRoutes.tsx').then((m) => ({ default: m.LogisticsRoutes }))));
const QualityRoutes = lazy(() => recoverLazyImport(() => import('./quality/QualityRoutes.tsx').then((m) => ({ default: m.QualityRoutes }))));
const MillingRoutes = lazy(() => recoverLazyImport(() => import('./milling/MillingRoutes.tsx').then((m) => ({ default: m.MillingRoutes }))));
const PipelineRoutes = lazy(() => recoverLazyImport(() => import('./pipeline/PipelineRoutes.tsx').then((m) => ({ default: m.PipelineRoutes }))));
const StockRoutes = lazy(() => recoverLazyImport(() => import('./stock/StockRoutes.tsx').then((m) => ({ default: m.StockRoutes }))));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Un refus (4xx) ne se corrige pas en réessayant.
      retry: (count, err) => !(err instanceof ApiError && err.problem.status >= 400 && err.problem.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

function RootProviders() {
  return (
    <AuthProvider>
      <TenantProvider>
        <UnsavedProvider>
          <GlobalNavigationGuard />
          <Outlet />
        </UnsavedProvider>
      </TenantProvider>
    </AuthProvider>
  );
}

const router = createBrowserRouter(createRoutesFromElements(
  <Route element={<RootProviders />}>
    <Route element={<AppShell />}>
                <Route index element={<HomePage />} />
                <Route path="compte" element={<MyAccountPage />} />
                <Route path="aide" element={<GuideLibraryPage />} />
                <Route path="formation" element={<TrainingPage />} />
                <Route path="comptes/*" element={<AccountsRoutes />} />
                <Route path="pipeline/*" element={<PipelineRoutes />} />
                <Route path="visites/*" element={<VisitsRoutes />} />
                <Route path="stock/*" element={<StockRoutes />} />
                <Route path="ventes/*" element={<SalesRoutes />} />
                <Route path="encaissements/*" element={<ReceivablesRoutes />} />
                <Route path="objectifs/*" element={<PerformanceRoutes />} />
                <Route path="reclamations/*" element={<ComplaintsRoutes />} />
                <Route path="analyses/*" element={<AnalyticsRoutes />} />
                <Route path="elevage/*" element={<LivestockRoutes />} />
                <Route path="logistique/*" element={<LogisticsRoutes />} />
                <Route path="qualite/*" element={<QualityRoutes />} />
                <Route path="meunerie/*" element={<MillingRoutes />} />
                <Route path="catalogue/*" element={<CatalogRoutes />} />
                <Route path="import/*" element={<ImportRoutes />} />
                <Route path="admin/*" element={<AdminHub />} />
                <Route path="callback" element={<Navigate to="/" replace />} />
                <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Route>,
));

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
